import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell } from "lucide-react";
import { formatDateTime } from "@/lib/utils";

// This said "the backend has written these all along — every approval, rejection and
// send-back". It had not: the approval step went in migration 0006 and took the only
// writer with it, so the panel opened on "Nothing yet." for every user forever.
// `abhyas/notify.py` is the writer now, and what it writes is addressed to the
// employee — nominated, moved, cancelled, certified, withdrawn — never to the HR user
// who caused it.
export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [failed, setFailed] = useState(false);
  // Until the first answer, an empty list is only a request still out — not "Nothing yet."
  const [loaded, setLoaded] = useState(false);
  // A request out right now. Without it, pressing Try again left "Could not load
  // notifications." up until the server answered, and the link looked as if it did nothing.
  const [busy, setBusy] = useState(false);
  const loadSeq = useRef(0);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const { user } = useAuth();

  const load = useCallback(async () => {
    // It asks on mount and again on every open, so two can be out at once. Only the newest
    // may answer: the mount load failing late put "Could not refresh" over the list that
    // opening the panel had just fetched.
    const mine = ++loadSeq.current;
    setBusy(true);
    try {
      const { data } = await api.get("/notifications");
      if (mine !== loadSeq.current) return;
      // A 200 that is not a list (an HTML error page) did not answer the question either.
      if (!Array.isArray(data)) throw new Error("unexpected response");
      setItems(data);
      setFailed(false);
    } catch (e) {
      if (mine !== loadSeq.current) return;
      // Refused outright while an account still has to set its password, which is a
      // normal state rather than an error worth surfacing. Only that refusal empties the
      // panel: a 500 or a timeout did too, and since this runs on every open, one failed
      // refresh wiped what had loaded and answered "Nothing yet." to somebody who had just
      // been nominated, or had a certificate withdrawn.
      if (e.response?.status === 403) { setItems([]); setFailed(false); }
      else setFailed(true);
    }
    setLoaded(true);
    setBusy(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const unread = items.filter((n) => !n.read).length;

  const openItem = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
        setItems((cur) => cur.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      } catch { /* reading it is not worth interrupting the person for */ }
    }
    // /trainings/:id is an HR screen — Protected bounces an Employee straight back to
    // /me, so for them the click was a flash and a return to where they already were.
    // The notification is the whole message for that reader; their own record is behind
    // the panel already.
    if (n.training_id && user?.role !== "Employee") {
      setOpen(false);
      nav(`/trainings/${n.training_id}`);
    }
  };

  const markAllRead = async () => {
    const pending = items.filter((n) => !n.read);
    await Promise.all(pending.map((n) => api.post(`/notifications/${n.id}/read`).catch(() => {})));
    setItems((cur) => cur.map((x) => ({ ...x, read: true })));
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell"
                aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}>
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[1rem] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-medium flex items-center justify-center"
                  data-testid="notification-count">{unread > 9 ? "9+" : unread}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="font-heading font-semibold text-sm">Notifications</div>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs text-primary hover:underline"
                    data-testid="notifications-mark-all">Mark all read</button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto divide-y divide-border">
          {!loaded && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="notifications-loading">
              Loading...
            </div>
          )}
          {failed && busy && (
            <div className={`px-4 text-sm text-muted-foreground ${items.length ? "py-3" : "py-8 text-center"}`}
                 data-testid="notifications-retrying">
              Loading...
            </div>
          )}
          {failed && !busy && (
            <div className={`px-4 text-sm ${items.length ? "py-3" : "py-8 text-center"}`} data-testid="notifications-error">
              <div className="text-destructive-text">
                {items.length ? "Could not refresh. These are from the last time they loaded." : "Could not load notifications."}
              </div>
              <button onClick={load} className="mt-1 text-xs text-primary hover:underline"
                      data-testid="notifications-retry">Try again</button>
            </div>
          )}
          {loaded && !failed && items.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="notifications-empty">
              Nothing yet.
            </div>
          )}
          {items.map((n) => (
            <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-${n.id}`}
                    className={`w-full text-left px-4 py-3 hover:bg-muted/50 ${n.read ? "" : "bg-primary/5"}`}>
              <div className={`text-sm ${n.read ? "text-muted-foreground" : "font-medium"}`}>{n.message}</div>
              <div className="text-xs text-muted-foreground mt-1">{formatDateTime(n.created_at)}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
